import { Alert, Button } from "antd"
import React, { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type QueryErrorAlertProps = {
    /** Bereits übersetzte, sichtbare Fehlermeldung */
    message: string
    /** Stößt die fehlgeschlagene Query erneut an; das Promise steuert den Loading-Zustand des Buttons */
    onRetry: () => Promise<unknown>
    /** data-testid des Alerts; der Retry-Button erhält automatisch `${testId}-retry` */
    testId: string
    /** Optionales Styling (z.B. Abstand zur darunterliegenden Ansicht) */
    style?: React.CSSProperties
}

/**
 * Einheitliches Fehler-Alert mit Retry-Button für fehlgeschlagene Queries.
 *
 * Retry-Feedback: Solange das onRetry-Promise läuft, zeigt der Button einen
 * Loading-Spinner und ist deaktiviert. Bewusst KEIN notifyOnNetworkStatusChange
 * an den Queries — z.B. pollt CoursesTable alle 10 Minuten mit cache-and-network,
 * loading=true bei jedem Poll schickte die Tabelle regelmäßig in den Spinner-Overlay.
 */
const QueryErrorAlert: FC<QueryErrorAlertProps> = ({ message, onRetry, testId, style }) => {
    const { t } = useTranslation()
    const [retryPending, setRetryPending] = useState(false)

    // Unmount-Schutz: Nach erfolgreichem Retry verschwindet das Alert in der Regel
    // (Fehler weg → Parent rendert es nicht mehr) — danach darf kein State mehr gesetzt werden.
    const isMountedRef = useRef(true)
    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    const handleRetry = async () => {
        setRetryPending(true)
        try {
            await onRetry()
        } catch {
            // Rejection bewusst schlucken: Schlägt auch der Retry fehl, bleibt der
            // Fehlerzustand über den Query-Hook sichtbar (keine unhandled rejection)
        } finally {
            if (isMountedRef.current) {
                setRetryPending(false)
            }
        }
    }

    return (
        <Alert
            data-testid={testId}
            type='error'
            showIcon
            style={style}
            message={message}
            action={
                <Button data-testid={`${testId}-retry`} size='small' danger loading={retryPending} disabled={retryPending} onClick={handleRetry}>
                    {t("common.retry", "Erneut versuchen")}
                </Button>
            }
        />
    )
}

export default QueryErrorAlert
